/**
 * @file src/components/CreateApiKeyModal/CreateApiKeyModal.tsx
 * @exports CreateApiKeyModal
 * @summary Modal for generating a new API key. Shows a name form on first open (with a note that the key inherits the current user's permissions) and displays the generated key (with copy action) after successful creation. Uses Mantine Modal for WCAG 2.1.1-compliant close-button keyboard handling (Space and Enter both fire the close handler). Keeps the generate action visible by constraining the organization list to the remaining viewport height.
 */

import {
	BodyLSecondary,
	BodyMEmphasis,
	BodyMPrimary,
	ButtonColorFamilyEnum,
	Form,
	HeadingH1,
	PrimaryButton,
	SubheadingMPrimary,
	TextButton,
	TextVariantEnum,
	Typography,
	colors,
	isEmptyObject,
} from "@cerebruminc/cerebellum";
import { Modal } from "@mantine/core";
import { type FormikProps, type FormikTouched, setNestedObjectValues } from "formik";
import upperFirst from "lodash/upperFirst";
import React, { useCallback, useRef, useState } from "react";
import { styled } from "styled-components";

import { showError, showMessage, showSuccess } from "src/helpers";
import { useGenerateApiKeyMutation, useMeQuery } from "src/generated/graphql";
import { useGetAllOrganizations } from "src/hooks";

import { getApiKeyScope } from "./apiKeyScope";

const PrefixText = styled(BodyMEmphasis)`
	margin-bottom: 8px;
	margin-top: 8px;
`;
const Divider = styled.div`
	background-color: ${colors.COOL_GREY_20};
	height: 1px;
	margin-bottom: 32px;
	margin-top: 32px;
`;
const KeyBox = styled.div`
	align-items: center;
	border-radius: 6px;
	border: solit 1px ${colors.COOL_GREY_25};
	display: flex;
	height: 56px;
	justify-content: center;
	margin-top: 28px;
  margin-bottom: 28px;
`;
const Actions = styled.div`
	align-items: center;
	display: flex;
	flex-direction: column;
	gap: 12px;
	justify-content: center;
`;
const FormLayout = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	min-height: 0;
`;
const FormMain = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	min-height: 0;
`;
const FormHeader = styled.div`
	margin-bottom: 24px;
`;
const FormSubtitle = styled.div`
	margin-top: 8px;
`;
const ScopeSection = styled.div`
	border-top: 2px solid ${colors.COOL_GREY_25};
	display: flex;
	flex: 1;
	flex-direction: column;
	margin-top: -74px;
	min-height: 0;
	padding-top: 32px;
`;
const ScopeBox = styled.div`
	background-color: ${colors.BLUE_5};
	border: 1px solid ${colors.BLUE_50};
	border-radius: 6px;
	display: flex;
	flex: 1;
	flex-direction: column;
	margin-top: 24px;
	min-height: 0;
	padding: 16px;
`;
const OrganizationList = styled.div`
	border: 1px solid ${colors.COOL_GREY_20};
	border-radius: 6px;
	flex: 1 1 auto;
	margin-top: 12px;
	max-height: 220px;
	min-height: 0;
	overflow-x: hidden;
	overflow-y: auto;
`;
const OrganizationRow = styled.div`
	align-items: center;
	background: ${colors.WHITE};
	display: flex;
	gap: 12px;
	justify-content: space-between;
	min-height: 42px;
	padding: 8px 12px;

	&:not(:last-child) {
		border-bottom: 1px solid ${colors.COOL_GREY_20};
	}
`;
const OrganizationName = styled.div`
	flex: 1;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;
const RoleBadge = styled.span`
	background-color: ${colors.COOL_GREY_10};
	border-radius: 100px;
	color: ${colors.COOL_GREY_85};
	flex-shrink: 0;
	font-size: 12px;
	font-weight: 600;
	line-height: 1.2;
	padding: 4px 10px;
	text-transform: uppercase;
	white-space: nowrap;
`;
const FormFooter = styled.div`
	align-items: center;
	display: flex;
	flex-direction: column;
	flex-shrink: 0;
	padding-top: 40px;
`;

type Props = {
	visible: boolean;
	onSuccess: () => void;
	onClose: () => void;
};

/**
 * Modal to render api key creation form & api key details
 */
export const CreateApiKeyModal = (props: Props) => {
	const formRef = useRef<FormikProps<any>>(null);
	const { visible, onSuccess, onClose } = props;
	const [showInfo, setShowInfo] = useState(false);
	const { data: meData } = useMeQuery();
	const { organizations: availableOrganizations } = useGetAllOrganizations();
	const scope = getApiKeyScope(meData?.me, availableOrganizations);

	const { mutateAsync: createApiKeyMutation, data, isLoading: creating } = useGenerateApiKeyMutation();

	const renderOrganizationList = () => (
		<OrganizationList>
			{scope.organizations.map(({ id, name, role }) => (
				<OrganizationRow key={id}>
					<OrganizationName title={name}>
						<Typography variant={TextVariantEnum.BodySSecondary}>{name}</Typography>
					</OrganizationName>
					{role ? <RoleBadge>{role.replaceAll("_", " ")}</RoleBadge> : null}
				</OrganizationRow>
			))}
		</OrganizationList>
	);

	/**
	 * close modal
	 * also reset show info boolean so that next time user open create modal it will be in inital stage
	 */
	const closeModal = useCallback(() => {
		setShowInfo(false);
		onClose();
	}, [onClose]);

	/**
	 * method to create api key
	 */
	const createApiKey = useCallback(
		async (params: { name: string }) => {
			try {
				await createApiKeyMutation({
					name: params.name,
				});
				onSuccess();
				showSuccess("Api Key created successfully");
				setShowInfo(true);
			} catch (error: any) {
				const message = upperFirst(error?.response?.data?.message?.[0] ?? error?.message ?? "An error occurred");
				showError(message);
			}
		},
		[createApiKeyMutation, onSuccess, setShowInfo],
	);

	const onSubmit = useCallback(
		(values: { name: string }) => {
			createApiKey(values);
		},
		[createApiKey],
	);

	const externalSubmit = async () => {
		if (formRef.current) {
			const errors = await formRef.current.validateForm();
			const noErrors = isEmptyObject(errors);
			if (noErrors) {
				formRef.current.handleSubmit();
			} else {
				formRef.current.setTouched(setNestedObjectValues<FormikTouched<any>>(errors, true));
			}
		}
	};

	/**
	 * render api key creation form
	 */
	const renderForm = () => {
		return (
			<FormLayout>
				<FormMain>
					<FormHeader>
						<HeadingH1 data-sentry-unmask>Add API Key</HeadingH1>
						<FormSubtitle>
							<Typography variant={TextVariantEnum.BodySSecondary}>
								This API key will inherit all of the permissions of your current user
							</Typography>
						</FormSubtitle>
					</FormHeader>
					<Form<{ name: string }>
						innerRef={formRef as React.RefObject<FormikProps<{ name: string }>>}
						fields={[
							{
								fieldLabel: "Name",
								name: "name",
								initialValue: "",
								options: {
									clearButton: true,
								},
								required: true,
							},
						]}
						submit={onSubmit}
						hideSubmitButton
						themeOverride={{ titleFontSize: 0, titleGap: 0, titleLetterSpacing: 0 }}
					/>
					<ScopeSection>
						{scope.hasBroadAccess ? (
							<>
								<Typography variant={TextVariantEnum.BodySEmphasis}>Organization access</Typography>
								<Typography variant={TextVariantEnum.BodySSecondary}>
									This API key will inherit your role permissions for {scope.organizations.length} organizations:
								</Typography>
							</>
						) : (
							<Typography variant={TextVariantEnum.BodySSecondary}>
								This API key will have access to <b>{scope.organizations[0]?.name ?? "your organization"}</b>.
							</Typography>
						)}
						{scope.hasBroadAccess ? (
							<ScopeBox role="alert">
								<Typography variant={TextVariantEnum.BodySEmphasis}>
									Warning: this key will have broad or elevated access. Only continue if every organization below is
									intended.
								</Typography>
								{renderOrganizationList()}
							</ScopeBox>
						) : null}
					</ScopeSection>
				</FormMain>
				<FormFooter>
					<PrimaryButton
						text="Generate API Key"
						onClick={externalSubmit}
						colorFamily={ButtonColorFamilyEnum.Blue}
						loading={creating}
						shadow
					/>
				</FormFooter>
			</FormLayout>
		);
	};

	/**
	 * render api key details once user create api key successfully
	 */
	const renderDetails = () => {
		return (
			<>
				<SubheadingMPrimary data-sentry-unmask>Api Key Generated</SubheadingMPrimary>
				<HeadingH1 data-sentry-unmask>Save your Api Key</HeadingH1>
				<PrefixText data-sentry-unmask>Prefix Identifier</PrefixText>
				<BodyMPrimary>{data?.generateApiKey.prefix}</BodyMPrimary>
				<Divider />
				<BodyMEmphasis data-sentry-unmask>Organization Access</BodyMEmphasis>
				{renderOrganizationList()}
				<Divider />
				<BodyMEmphasis data-sentry-unmask>API Key</BodyMEmphasis>
				<BodyLSecondary textColor={colors.RED_80} data-sentry-unmask>
					This will only visible once! <b>Save it now!</b>
				</BodyLSecondary>
				<KeyBox>{data?.generateApiKey.apiKey}</KeyBox>
				<Actions>
					<PrimaryButton
						text="Copy API Key to Clipboard"
						onClick={() => {
							navigator.clipboard.writeText(data?.generateApiKey.apiKey as string);
							showMessage("Copied to Clipboard");
						}}
					/>
					<TextButton text="Close" onClick={closeModal} />
				</Actions>
			</>
		);
	};

	return (
		<Modal
			onClose={closeModal}
			opened={visible}
			size={518}
			styles={{
				body: {
					display: "flex",
					flex: 1,
					flexDirection: "column",
					minHeight: 0,
					overflow: showInfo ? "auto" : "hidden",
					padding: "0px 66px 64px",
				},
				content: {
					display: "flex",
					flexDirection: "column",
					maxHeight: "calc(100dvh - 48px)",
				},
			}}
			transitionProps={{ transition: "slide-down" }}
			withCloseButton
		>
			{visible ? (!showInfo ? renderForm() : renderDetails()) : null}
		</Modal>
	);
};
